/**
 * Entry hooks for React Query
 *
 * Provides data fetching hooks for entry management.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  listEntries,
  getEntry,
  updateEntry,
  getEntryAssociations,
  getEntryStats,
} from '@/api/entries';
import type { EntryFilters, UpdateEntryInput } from '@/types/entry';

const ENTRIES_KEY = 'entries';
const ENTRY_KEY = 'entry';
const ENTRY_ASSOCIATIONS_KEY = 'entry-associations';
const ENTRY_STATS_KEY = 'entry-stats';

/**
 * Fetch entries with pagination (infinite scroll).
 */
export function useEntries(filters: EntryFilters = {}, pageSize = 20) {
  return useInfiniteQuery({
    queryKey: [ENTRIES_KEY, filters],
    queryFn: async ({ pageParam = 0 }) => {
      return listEntries(filters, {
        limit: pageSize,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalFetched = allPages.reduce((acc, page) => acc + page.entries.length, 0);
      if (totalFetched >= lastPage.total) {
        return undefined;
      }
      return totalFetched;
    },
    initialPageParam: 0,
    staleTime: 30000,
  });
}

/**
 * Fetch entries with simple pagination (offset-based).
 */
export function useEntriesPaginated(
  filters: EntryFilters = {},
  page = 0,
  pageSize = 20
) {
  return useQuery({
    queryKey: [ENTRIES_KEY, 'paginated', filters, page, pageSize],
    queryFn: () =>
      listEntries(filters, {
        limit: pageSize,
        offset: page * pageSize,
      }),
    staleTime: 30000,
  });
}

/**
 * Fetch a single entry by ID.
 */
export function useEntry(entryId: string | null, includeAssociations = true) {
  return useQuery({
    queryKey: [ENTRY_KEY, entryId, includeAssociations],
    queryFn: async () => {
      if (!entryId) {
        throw new Error('Entry ID is required');
      }
      return getEntry(entryId, includeAssociations);
    },
    enabled: !!entryId,
    staleTime: 60000,
  });
}

/**
 * Fetch associations for an entry.
 */
export function useEntryAssociations(entryId: string | null) {
  return useQuery({
    queryKey: [ENTRY_ASSOCIATIONS_KEY, entryId],
    queryFn: async () => {
      if (!entryId) {
        throw new Error('Entry ID is required');
      }
      return getEntryAssociations(entryId);
    },
    enabled: !!entryId,
    staleTime: 60000,
  });
}

/**
 * Fetch entry statistics.
 */
export function useEntryStats() {
  return useQuery({
    queryKey: [ENTRY_STATS_KEY],
    queryFn: getEntryStats,
    staleTime: 60000,
  });
}

/**
 * Mutations for entry operations.
 */
export function useEntryMutations() {
  const queryClient = useQueryClient();

  const update = useMutation({
    mutationFn: ({ entryId, input }: { entryId: string; input: UpdateEntryInput }) =>
      updateEntry(entryId, input),
    onSuccess: (data, variables) => {
      // Update the specific entry in cache
      queryClient.setQueryData([ENTRY_KEY, variables.entryId, true], data);
      queryClient.setQueryData([ENTRY_KEY, variables.entryId, false], data);
      // Invalidate entries list
      queryClient.invalidateQueries({ queryKey: [ENTRIES_KEY] });
      // Invalidate stats
      queryClient.invalidateQueries({ queryKey: [ENTRY_STATS_KEY] });
    },
  });

  return {
    updateEntry: update,
  };
}
